import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { z } from 'zod';
import { SupportedChainId, utils } from '@orionprotocol/sdk';
import { isChainInCurrentEnvironment } from '@orionprotocol/utils';
import {
  envInfoSchema,
  chainInfoSchema,
  environment,
  envs,
  invariant,
  keyValueStorage,
} from '@orionprotocol/common';

const { isValidChainId } = utils;
export type AppState = {
  env?: string;
  envs: Partial<Record<string, z.infer<typeof envInfoSchema>>>;
  chains: Partial<Record<SupportedChainId, z.infer<typeof chainInfoSchema>>>;
  chainId: SupportedChainId;
  priceIntervalUpdated: boolean;
  nativeCurrencyWrappedTokenAddress: string;
  isShownChangeNetworkPopup: boolean;
  isCustomConfig?: boolean;
  forceEnableAllFeatures: boolean;
  mobileMenuIsOpen: boolean;
};

let initialChainId: SupportedChainId;

const lsAppChainId = keyValueStorage.getItem('appChainId');
if (lsAppChainId && utils.isValidChainId(lsAppChainId)) {
  // const isChainInCurrentEnvironment = envs[environment].networks.some((network) => network.chainId === lsAppChainId);
  // if (isChainInCurrentEnvironment)
  initialChainId = lsAppChainId;
  // else initialChainId = envs[environment].defaultNetwork;
} else {
  invariant(envs);
  const initialEnvironment = envs[environment];
  invariant(initialEnvironment, 'No initial environment');

  const firstNetwork = Object.keys(initialEnvironment.networks)[0];
  invariant(isValidChainId(firstNetwork));
  initialChainId = initialEnvironment.defaultNetwork ?? firstNetwork;
}
const isShownChangeNetworkPopup =
  keyValueStorage.getItem('isShownChangeNetworkPopup') !== 'false';

const initialState: AppState = {
  envs: {},
  chains: {},
  chainId: initialChainId,
  priceIntervalUpdated: true,
  nativeCurrencyWrappedTokenAddress: '',
  isShownChangeNetworkPopup,
  forceEnableAllFeatures: false,
  mobileMenuIsOpen: false,
};

export const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    setIsCustomConfig(state, action: PayloadAction<boolean>) {
      state.isCustomConfig = action.payload;
    },
    addEnvironment(
      state,
      action: PayloadAction<{
        envLabel: string;
        env: z.infer<typeof envInfoSchema>;
      }>,
    ) {
      state.envs[action.payload.envLabel] = action.payload.env;
    },
    setEnvironment(state, action: PayloadAction<string>) {
      state.env = action.payload;
    },
    setEnvironments(
      state,
      action: PayloadAction<
        Partial<Record<string, z.infer<typeof envInfoSchema>>>
      >,
    ) {
      state.envs = action.payload;
    },
    setChains(
      state,
      action: PayloadAction<
        Partial<Record<SupportedChainId, z.infer<typeof chainInfoSchema>>>
      >,
    ) {
      state.chains = action.payload;
    },
    setAppNetwork(state, action: PayloadAction<string>) {
      const chainId = action.payload;

      if (
        state.env &&
        isChainInCurrentEnvironment(state.envs, state.env, chainId)
      ) {
        state.chainId = chainId;
      }
    },
    setPriceIntervalUpdated(state, action: PayloadAction<boolean>) {
      state.priceIntervalUpdated = action.payload;
    },
    setNativeCurrencyWrappedTokenAddress(state, action: PayloadAction<string>) {
      state.nativeCurrencyWrappedTokenAddress = action.payload;
    },
    disableChangeNetworkPopup(state) {
      state.isShownChangeNetworkPopup = false;
    },
    setForceEnableAllFeatures(state) {
      state.forceEnableAllFeatures = true;
    },
    setMobileMenuIsOpen(state, action: PayloadAction<boolean>) {
      state.mobileMenuIsOpen = action.payload;
    },
  },
});

export const appReducer = appSlice.reducer;
